import React from "react";
import { Link } from "react-router-dom";
import { Avatar } from "../../../design/avatar";
import { Badge } from "../../../design/badge";
import { getInitials } from "../../../utils/string";
import { Case } from "@visitproof/types";
import {
  FolderIcon,
  UserGroupIcon,
  UserIcon,
  CalendarDaysIcon,
  CheckCircleIcon,
  XCircleIcon
} from "@heroicons/react/20/solid";

interface CaseGridProps {
  cases: Case[];
}

const CaseGrid: React.FC<CaseGridProps> = ({ cases }) => {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {cases.map((caseItem) => (
        <CaseCard key={caseItem.id} caseItem={caseItem} />
      ))}
    </div>
  );
};

interface CaseCardProps {
  caseItem: Case;
}

const CaseCard: React.FC<CaseCardProps> = ({ caseItem }) => {
  const parents = caseItem.parents ? Object.values(caseItem.parents) : [];
  const children = caseItem.children ? Object.values(caseItem.children) : [];

  const stats = [
    { label: "Scheduled", value: caseItem.numberOfScheduledVisits || 0, icon: CalendarDaysIcon },
    { label: "Completed", value: caseItem.numberOfCompletedVisits || 0, icon: CheckCircleIcon },
    { label: "Canceled", value: caseItem.numberOfCanceledVisits || 0, icon: XCircleIcon },
  ];

  return (
    <Link
      to={`/case/${caseItem.id}`}
      className="group block p-5 bg-white border rounded-xl shadow-sm transition-all duration-200 hover:shadow-md hover:border-blue-300 dark:bg-zinc-900 dark:border-zinc-700 dark:hover:border-blue-600"
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-blue-50 dark:bg-blue-900/30">
            <FolderIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <h3 className="font-semibold text-zinc-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
              {caseItem.identifier || "Unnamed Case"}
            </h3>
          </div>
        </div>
        <Badge color={caseItem.status === "open" ? "green" : "zinc"}>
          {caseItem.status}
        </Badge>
      </div>

      {/* Parents Section */}
      <div className="mb-3">
        <div className="flex items-center gap-2 mb-2 text-xs font-medium text-zinc-500 dark:text-zinc-400">
          <UserGroupIcon className="w-4 h-4" />
          <span>Parents ({parents.length})</span>
        </div>
        {parents.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {parents.slice(0, 3).map((parent, idx) => (
              <div key={idx} className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-zinc-100 dark:bg-zinc-800">
                <Avatar
                  src={parent.avatarUrl}
                  className="size-5"
                  initials={getInitials(`${parent.firstName} ${parent.lastName}`)}
                />
                <span className="text-xs text-zinc-700 dark:text-zinc-300">
                  {parent.firstName}
                </span>
              </div>
            ))}
            {parents.length > 3 && (
              <span className="px-2 py-1 text-xs text-zinc-500 dark:text-zinc-400">
                +{parents.length - 3} more
              </span>
            )}
          </div>
        ) : (
          <span className="text-xs text-zinc-400 dark:text-zinc-500">No parents assigned</span>
        )}
      </div>

      {/* Children Section */}
      <div className="mb-4">
        <div className="flex items-center gap-2 mb-2 text-xs font-medium text-zinc-500 dark:text-zinc-400">
          <UserIcon className="w-4 h-4" />
          <span>Children ({children.length})</span>
        </div>
        {children.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {children.slice(0, 3).map((child, idx) => (
              <div key={idx} className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-zinc-100 dark:bg-zinc-800">
                <Avatar
                  src={child.avatarUrl}
                  className="size-5"
                  initials={getInitials(`${child.firstName} ${child.lastName}`)}
                />
                <span className="text-xs text-zinc-700 dark:text-zinc-300">
                  {child.firstName}
                </span>
              </div>
            ))}
            {children.length > 3 && (
              <span className="px-2 py-1 text-xs text-zinc-500 dark:text-zinc-400">
                +{children.length - 3} more
              </span>
            )}
          </div>
        ) : (
          <span className="text-xs text-zinc-400 dark:text-zinc-500">No children assigned</span>
        )}
      </div>

      {/* Stats */}
      <div className="pt-3 border-t border-zinc-100 dark:border-zinc-800">
        <div className="grid grid-cols-3 gap-2">
          {stats.map((stat) => (
            <div key={stat.label} className="text-center">
              <div className="flex items-center justify-center gap-1">
                <stat.icon className="w-3.5 h-3.5 text-zinc-400" />
                <span className="text-sm font-semibold text-zinc-900 dark:text-white">
                  {stat.value}
                </span>
              </div>
              <span className="text-xs text-zinc-500 dark:text-zinc-400">{stat.label}</span>
            </div>
          ))}
        </div>
      </div>
    </Link>
  );
};

export default CaseGrid;
